<template>
    <div class="list-underlag">
        <KHeader heading="Underlag">
            <KButton variant="hollow" size="slim" :icon-path="PlusCircleBlue" @click="() => openForm()">
                Skapa nytt underlag
            </KButton>
        </KHeader>

        <KFilter name="underlag-filter" :schema="filterSchema" @submit="onFilter" />

        <KTable
            paginator
            alwaysShowPaginator
            name="underlag-table"
            :columns="columns"
            :actions="tableActions"
            :value="rows"
            :loading="isLoading"
            :totalRecords="count"
            :first="first"
            @page="onPage"
        />
    </div>
</template>

<script lang="ts">
    import tabIcon from "@/assets/narvarostatistik.svg";

    export default {
        name: "ListUnderlag",
        tabName: "Underlag",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
    import { openDialog } from "@/utils";
    import KFilter from "@/components/KFilter/KFilter.vue";
    import KTable from "@/components/KTable/KTable.vue";
    import KButton from "@/components/Button.vue";
    import KHeader from "@/components/KHeader.vue";
    import PlusCircleBlue from "@/assets/plus_circle_blue.svg";
    import FormUnderlag from "@/tabs/Skreg/Underlag/FormUnderlag.vue";
    import { useStore } from "@/store";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";

    const store = useStore();

    const isLoading = ref(false);
    const filterParams = ref<Record<string, any>>({});
    const subsetName = ref("default");
    const page = ref(1);
    const limit = ref(20);
    // Antalet rader som finns för subsetet, som paginatorn räknar sidor på
    const count = computed(() => store.getters["skreg/underlag/underlagSubsetTotal"]({ subsetName: subsetName.value }));

    const first = computed(() => (page.value - 1) * limit.value);

    const newlyCreated = computed(() =>
        store.getters["skreg/underlag/underlagSubset"]({
            subsetName: "newlyCreated",
            offset: 0,
            limit: 0,
            toArray: false,
        })
    );
    const newlyUpdated = computed(() =>
        store.getters["skreg/underlag/underlagSubset"]({
            subsetName: "newlyUpdated",
            offset: 0,
            limit: 0,
            toArray: false,
        })
    );

    watch([() => newlyCreated.value.size, () => newlyUpdated.value.size], () => {
        fetchList();
    });

    const rows = computed(() =>
        store.getters["skreg/underlag/underlagSubset"]({
            subsetName: subsetName.value,
            offset: first.value,
            limit: limit.value,
        }).map((u: any) => ({
            ...u,
            tableInfo: {
                isNewlyCreated: !!newlyCreated.value.get(u.id),
                isNewlyUpdated: !!newlyUpdated.value.get(u.id),
            },
        }))
    );

    const filterSchema = generateSchema({
        addToAll: { ignoreEmptyData: true },
        schema: [{ component: "KText", name: "namn", label: "Namn" }],
    });

    const columns = [{ field: "namn", header: "Namn" }];

    const tableActions = [
        {
            title: "Redigera underlag",
            show: () => true,
            callback: ({ data }: { data: any }) => openForm(data),
        },
    ];

    const fetchList = async () => {
        isLoading.value = true;
        try {
            const { subsetName: fetchedSubset } = await store.dispatch("skreg/underlag/fetchUnderlag", {
                page: page.value,
                limit: limit.value,
                filterParams: filterParams.value,
            });
            subsetName.value = fetchedSubset;
        } catch {
            openDialog("Kunde inte hämta underlag.", "warning");
        } finally {
            isLoading.value = false;
        }
    };

    const onFilter = ({ formData }: { formData: any }) => {
        filterParams.value = formData;
        page.value = 1;
        fetchList();
    };

    const onPage = ({ page: newPage, rows: newRows }: { page: number; rows: number }) => {
        page.value = newPage;
        limit.value = newRows;
        fetchList();
    };

    const openForm = (item = undefined) => {
        store.dispatch("tabs/openTab", {
            component: FormUnderlag,
            parameters: item,
        });
    };

    onMounted(async () => {
        await store.dispatch("skreg/underlag/addSubscriber");
        await fetchList();
    });

    onBeforeUnmount(async () => {
        await store.dispatch("skreg/underlag/removeSubscriber");
    });
</script>

<style lang="scss" scoped></style>
